import React from "react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import RevealImage from "../components/RevealImage";
import Cover from "../components/Cover";

const ENTRIES = [
  {
    coverType: "magazine",
    image: "/images/chandroday/full/7.jpg",
    source: "A + D",
    issueNo: "41",
    year: "2024",
    type: "Feature",
    title: "Chandroday",
    subtitle: "A house in a mango orchard",
    excerpt:
      "A twelve-page feature documenting the residence at Bhavnagar — built without felling a single tree of the existing orchard.",
    place: "Architecture + Design · India",
    href: "#",
  },
  {
    coverType: "magazine",
    image: "/images/veiled-house/full/1.jpg",
    source: "DOMUS",
    issueNo: "09",
    year: "2023",
    type: "Interview",
    title: "In Conversation",
    subtitle: "Nishant Machhar",
    excerpt:
      "On inheriting a practice, the architecture of small cities, and drawing with both instinct and restraint.",
    place: "Domus India · Mumbai",
    href: "#",
  },
  {
    coverType: "magazine",
    image: "/images/veiled-house/full/25.jpg",
    source: "IA+B",
    issueNo: "137",
    year: "2022",
    type: "Feature",
    title: "The Veiled House",
    subtitle: "Privacy as architecture",
    excerpt:
      "A long-read on the architectural wire-mesh façade and how it negotiates between openness and seclusion.",
    place: "Indian Architect & Builder",
    href: "#",
  },
  {
    coverType: "book",
    source: "CEPT Research Press",
    year: "2021",
    type: "Essay",
    title: "Contextual Modernism",
    subtitle: "Essays on architecture in Western India",
    author: "— Nishant Machhar",
    excerpt:
      "Notes on designing for the climate and culture of Saurashtra — written for the Master of Design program at CEPT.",
    place: "CEPT Research Journal · Ahmedabad",
    bg: "bg-[#121212]",
    href: "#",
  },
  {
    coverType: "poster",
    source: "Pool Magazine · Delhi",
    year: "2020",
    type: "Essay",
    title: "On Mobility",
    subtitle: "Transportation design and the grammar of built form",
    excerpt:
      "How transportation design and architecture share a common grammar of movement, scale, and human comfort.",
    place: "Pool Magazine · Delhi",
    href: "#",
  },
  {
    coverType: "book",
    source: "JIIA Journal",
    year: "2018",
    type: "Retrospective",
    title: "Nirvan Tirth",
    subtitle: "The quiet crematorium",
    author: "— Retrospective",
    excerpt:
      "A retrospective of the JIIA-awarded electric crematorium at Bhavnagar, twenty-four years after completion.",
    place: "Journal of the Indian Institute of Architects",
    bg: "bg-[#0C0C0C]",
    href: "#",
  },
  {
    coverType: "poster",
    source: "APIED · Vallabh Vidyanagar",
    year: "2017",
    type: "Lecture",
    title: "Architecture as Response",
    subtitle: "Annual juror lecture — Master of Architecture studio",
    excerpt:
      "A talk delivered to the Master of Architecture studio on reading a site before drawing one.",
    place: "Arvind Bhai Patel Institute of Environmental Design",
    href: "#",
  },
];

export default function Publications() {
  return (
    <>
      <Seo
        title="Publications"
        description="Features, interviews, essays and lectures by RM.NM Architects."
      />

      {/* Header */}
      <section className="container-page pt-16 md:pt-24 pb-12 md:pb-20">
        <Reveal>
          <div className="eyebrow mb-6">Publications</div>
          <h1 className="font-display text-[2.5rem] sm:text-6xl md:text-8xl tracking-wider sm:tracking-widest2 leading-[0.95]">
            Writings &amp;<br />Features
          </h1>
          <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed text-smoke">
            A selection of features, interviews, essays and lectures — on the studio's projects
            and by the architects themselves. For a full press kit, write to
            {" "}
            <a href="mailto:contact@rcmnrm.co.in" className="underline underline-offset-4 hover:opacity-60">
              contact@rcmnrm.co.in
            </a>.
          </p>
        </Reveal>
      </section>

      {/* Grid of cover-led entries */}
      <section className="container-page pb-24 md:pb-40">
        <Reveal className="flex items-end justify-between mb-8 md:mb-12">
          <div className="eyebrow">Selected — 2017 to present</div>
          <div className="text-[11px] tracking-widest3 uppercase text-smoke">
            {ENTRIES.length} entries
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-10 gap-y-14 md:gap-y-20">
          {ENTRIES.map((e, i) => (
            <Reveal key={`${e.year}-${e.title}`} delay={Math.min(i * 0.06, 0.3)}>
              <a
                href={e.href}
                target={e.href === "#" ? undefined : "_blank"}
                rel={e.href === "#" ? undefined : "noreferrer"}
                className="group block"
              >
                <RevealImage aspect="aspect-[3/4]" className="!bg-ink" delay={Math.min(i * 0.05, 0.25)}>
                  <div className="absolute inset-0 transition-transform duration-[900ms] ease-smooth group-hover:-translate-y-1">
                    <Cover item={e} />
                  </div>
                </RevealImage>

                <div className="mt-6 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 text-[11px] tracking-widest3 uppercase text-smoke">
                      <span>{e.year}</span>
                      <span className="w-4 h-px bg-mist" />
                      <span>{e.type}</span>
                    </div>
                    <h2 className="mt-3 font-display text-xl md:text-2xl tracking-widest2 leading-tight">
                      {e.title}
                      {e.subtitle && (
                        <span className="block text-sm md:text-base tracking-widest2 text-smoke mt-1">
                          — {e.subtitle}
                        </span>
                      )}
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-smoke">{e.excerpt}</p>
                    <div className="mt-4 text-[11px] tracking-widest3 uppercase text-smoke">
                      {e.place}
                    </div>
                  </div>
                  <span className="shrink-0 text-[11px] tracking-widest3 uppercase text-smoke group-hover:text-ink transition-colors">
                    Read →
                  </span>
                </div>
              </a>
            </Reveal>
          ))}
        </div>

        <div className="mt-12 md:mt-16 text-[11px] tracking-widest3 uppercase text-smoke">
          — End of list. More entries forthcoming.
        </div>
      </section>

      {/* Awards / Academia */}
      <section className="container-page pb-24 md:pb-40 border-t border-mist pt-16 md:pt-24">
        <Reveal className="mb-10 md:mb-14">
          <div className="eyebrow mb-4">Recognition &amp; Academia</div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            {
              year: "1994",
              title: "JIIA Award for Excellence in Architecture",
              note: "Nirvan Tirth Electric Crematorium, Bhavnagar — Mumbai",
            },
            {
              year: "1996",
              title: "Architect of the Year — Commendation",
              note: "Religious Building · The J K Cement Works — New Delhi",
            },
            {
              year: "Since 2004",
              title: "Visiting Juror — IPSA",
              note: "Indubhai Parekh School of Architecture",
            },
          ].map((item, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="border-t border-ink pt-6">
                <div className="eyebrow mb-4">{item.year}</div>
                <h3 className="font-display text-xl md:text-2xl tracking-wide leading-snug">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm text-smoke">{item.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
